import dayjs from "dayjs"
import { postcodeValidation } from "@soenergy/frontend-library/src/helpers/postcodeValidationHelper"
import AuthenticationService from "@soenergy/frontend-library/src/services/AuthenticationService"
import UserService from "@/services/UserService"
import ErrorReporter from "@soenergy/frontend-library/src/services/ErrorReporter"
import GatewayPortal from "@/services/GatewayPortal"
import { sessionStore } from "@soenergy/frontend-library/src/services/StorageFactory"
import { appointmentTypeMapping, InstallEligibilityReasons } from "@/constants"
import TrackingService from "@soenergy/frontend-library/src/services/TrackingService"

export const mutations = {
  SET_JUNIFER_DATA(state, value) {
    state.junifer = value
  },
  SET_ACCOUNTS(state, value) {
    state.accounts = value
  },
  SET_METER_REPAIR_ELIGIBILITY(state, value) {
    state.meterRepairEligibility = value
  },
  SET_METER_INSTALL_ELIGIBILITY(state, value) {
    state.meterInstallEligibility = value
  },
  SET_BOOKING_DATA(state, data) {
    state.bookingData = data
  },
  SET_CUSTOMER_ID(state, data) {
    if (data !== state.customerId) {
      Object.values(state.questionnaire).forEach((item) => {
        item.answer = typeof item.answer === "boolean" ? false : null
      })
      state.confirmationList.forEach((item) => (item.value = false))
    }
    state.customerId = data
    sessionStore.setItem("customerId", data)
  },
  SET_JUNIFER_ACCOUNT_ID(state, data) {
    state.juniferAccountId = data
    sessionStore.setItem("juniferAccountId", data)
  },
  SET_AUTHENTICATED(state, data) {
    state.authenticated = data
  },
  UPDATE_LOADING(state, isLoading) {
    state.loading = isLoading
  },
  SET_AUTH_ERROR(state, show) {
    state.showAuthError = show
  },
  SET_SUBMIT_STATUS(state, status) {
    state.submitStatus = status
  },
  SET_SUBMIT_ERROR(state, error) {
    state.submitError = error
  },
  SET_REGISTER_INTEREST_FLOW(state, data) {
    state.isRegisterInterestFlow = data
    sessionStore.setItem("isRegisterInterestFlow", data)
  },
  SET_AVAILABILITY_SLOTS(state, availabilitySlots) {
    state.availabilitySlots = availabilitySlots
  },
  SET_IS_AVAILABLE_SLOTS(state, isAvailableSlots) {
    state.isAvailableSlots = isAvailableSlots
  },
  SET_GAS_METER_LOCATED_ANSWER(state, answer) {
    state.questionnaire["gasMeterLocation"].answer = answer
  },
  SET_PARKING_DETAILS_ANSWER(state, answer) {
    state.questionnaire["parkingDetails"].answer = answer
  },
  SET_PET_DETAILS_ANSWER(state, answer) {
    state.questionnaire["petDetails"].answer = answer
  },
  SET_DISTANCE_BETWEEN_METERS_ANSWER(state, answer) {
    state.questionnaire["distanceBetweenMeters"].answer = answer
  },
  SET_METER_ABOVE_HEAD_HEIGHT_ANSWER(state, answer) {
    state.questionnaire["meterAboveHeadHeight"].answer = answer
  },
  SET_LESS_THAN_SIX_INCHES_AROUND_METER_ANSWER(state, answer) {
    state.questionnaire["lessThanSixInchesAroundMeter"].answer = answer
  },
  SET_METER_READING_FREQUENCY(state, frequency) {
    state.meterReadingFrequency = frequency
  },
}

export const actions = {
  validateStepTitle({ commit, state }) {
    if (state.bookingData.length) {
      const isSingle =
        state.bookingData[0].fuel_type === "ELEC_ONLY" ||
        state.bookingData[0].fuel_type === "GAS_ONLY"
      let yourMeterStepTitle = ""
      let pageIndex = 0

      for (let i = 0; i < state.steps.length; i++) {
        if (state.steps[i].name === "YourMeter") {
          yourMeterStepTitle = state.steps[i].title
          pageIndex = i
        }
      }

      let titleChange = isSingle ? yourMeterStepTitle : yourMeterStepTitle + "s"
      commit("SET_STEP_TITLE", { pageIndex, value: titleChange })
    }
  },
  async logout() {
    await AuthenticationService.logout(true)
    window.location.assign(process.env.VUE_APP_WEBSITE_URL)
  },
  async fetchJuniferData({ commit }) {
    await UserService.fetchJunifer()
      .then((response) => {
        if (response.data) {
          commit("SET_JUNIFER_DATA", response.data)

          if (response.data.accounts) {
            const accounts = Object.values(response.data.accounts)
            commit("SET_ACCOUNTS", accounts)
          }
        }
      })
      .catch((err) => {
        ErrorReporter.report(err)
      })
  },
  async fetchVulnerabilityNote({ state }) {
    try {
      const psrResponse = await GatewayPortal.getCustomerPsr(
        state.junifer.customer.id
      )
      const activeVulnerabilities = (
        psrResponse.data.customerVulnerabilities ?? []
      ).filter((v) => !v.endDate || dayjs(v.endDate).isAfter(dayjs()))
      if (!activeVulnerabilities.length) return ""
      const labels = activeVulnerabilities.map((v) => v.label).join(", ")
      return `. This customer or someone they live with has the following vulnerabilities: ${labels}`
    } catch (err) {
      ErrorReporter.report(err)
      return ""
    }
  },
  async bookAppointment({ commit, dispatch, state, getters }, appointmentType) {
    const vulnerabilityNote = await dispatch("fetchVulnerabilityNote")
    state.questionnaire.jobNote.answer = getters.jobNote + vulnerabilityNote

    const questionnaire = Object.values(state.questionnaire)
      .filter((questionObject) => questionObject.answer)
      .map((questionObject) => {
        return {
          question: questionObject.question,
          answer: questionObject.answer.toString(),
        }
      })
    const payload = {
      "aes-patch-availability-id":
        state.appointment.selectedAppointmentSlot.time.id,
      "time-slot": state.appointment.selectedAppointmentSlot.time["time-slot"],
      "slot-start-date-time": getters.startDateTime,
      "slot-end-date-time": getters.endDateTime,
      "customer-id": state.junifer.customer.id.toString(),
      "customer-first-name": state.homeDuringAppointment.form.firstName,
      "customer-last-name": state.homeDuringAppointment.form.lastName,
      "customer-email": state.homeDuringAppointment.form.email,
      "customer-phone": state.homeDuringAppointment.form.telephone,
      "customer-address": getters.address,
      "customer-city": getters.city,
      "customer-postcode": getters.validatedPostcode,
      "junifer-account-id": state.juniferAccountId.toString(),
      "gas-meter-serial-number": getters.gasMeterSerialNumber,
      "electricity-meter-serial-number": getters.electricityMeterSerialNumber,
      "booking-source": "Portal",
      mpan:
        state.meterInstallEligibility?.reason ===
        InstallEligibilityReasons.ELIGIBLE_GAS_EXCHANGE
          ? ""
          : getters.mpan,
      mprn:
        state.meterInstallEligibility?.reason ===
        InstallEligibilityReasons.ELIGIBLE_ELEC_EXCHANGE
          ? ""
          : getters.mprn,
      "fuel-type":
        {
          [InstallEligibilityReasons.ELIGIBLE_ELEC_EXCHANGE]: "electricity",
          [InstallEligibilityReasons.ELIGIBLE_GAS_EXCHANGE]: "gas",
          [InstallEligibilityReasons.ELIGIBLE_DUAL_EXCHANGE]: "dual",
        }[state.meterInstallEligibility?.reason] ?? "",
      "meter-reading-frequency": state.meterReadingFrequency,
      mop: getters.mopCode,
      questionnaire,
    }

    if (getters.isEligibleForRepair && appointmentType === "repair") {
      payload["appointment-type"] = getters.appointmentType
    }

    commit("SET_SUBMIT_STATUS", "PENDING")

    try {
      await GatewayPortal.createAppointment(payload, state.customerId)
      TrackingService.sendEvent("smbp-create-booking-successful", {
        appointmentType,
      })
      commit("SET_SUBMIT_STATUS", "SUCCESS")
    } catch (error) {
      commit("SET_SUBMIT_STATUS", "ERROR")
      commit("SET_SUBMIT_ERROR", error?.response?.data?.message)
      throw error
    }
  },
  async getAccountId({ commit }) {
    try {
      const response = await UserService.getAccountId()
      if (response?.data) {
        const accountId = response.data.current_account_id
        commit("SET_CUSTOMER_ID", accountId)

        const juniferAccountId =
          response.data.junifer_current_account_id || accountId
        commit("SET_JUNIFER_ACCOUNT_ID", juniferAccountId)

        if (!accountId) {
          commit("SET_AUTH_ERROR", true)
        }
      }
    } catch (err) {
      ErrorReporter.report(err)
      throw err
    }
  },
  async getMeterInstallEligibility({ commit, getters }) {
    await GatewayPortal.getMeterInstallEligibility(getters.accountNumber)
      .then((response) => {
        if (response.data) {
          commit("SET_METER_INSTALL_ELIGIBILITY", response.data)
        }
      })
      .catch((err) => {
        ErrorReporter.report(err)
      })
  },
  async getMeterRepairEligibility({ state, commit }) {
    await GatewayPortal.getMeterRepairEligibility(state.juniferAccountId)
      .then((response) => {
        if (response.data) {
          commit("SET_METER_REPAIR_ELIGIBILITY", response.data)
        }
      })
      .catch((err) => {
        ErrorReporter.report(err)
      })
  },
  async getAppointmentEligibility(
    { dispatch },
    checkMeterInstallEligibility = true
  ) {
    const promises = [dispatch("getMeterRepairEligibility")]

    if (checkMeterInstallEligibility) {
      promises.push(dispatch("getMeterInstallEligibility"))
    }

    await Promise.allSettled(promises)
  },
}

const findMeterpoint = (state, type) =>
  Object.values(
    state.junifer.accounts[state.customerId]?.meterpoints ?? {}
  ).find((mp) => mp.type === type) ?? null

export const getters = {
  customerId(state) {
    return Boolean(state.customerId)
  },
  isAvailableSlots(state) {
    return state.isAvailableSlots
  },
  isAuthenticated(state) {
    return Boolean(state.customerId && state.authenticated)
  },
  hasActiveAgreements(state) {
    const today = dayjs().endOf("day")
    return state.accounts.some((account) => {
      return account.agreements.some(
        (agreement) =>
          !agreement.cancelled &&
          (dayjs().isBetween(
            dayjs(agreement.fromDt),
            agreement.toDt ? dayjs(agreement.toDt) : today,
            "()"
          ) ||
            dayjs(agreement.fromDt).isAfter(today))
      )
    })
  },
  isSwitchingAway(state) {
    return (
      state.meterInstallEligibility?.reason ===
      InstallEligibilityReasons.INELIGIBLE_SWITCHING_AWAY
    )
  },
  hasSmartMeter(state) {
    return (
      state.meterInstallEligibility?.reason ===
      InstallEligibilityReasons.INELIGIBLE_ALREADY_SMART
    )
  },
  ineligibleTooComplex(state) {
    return (
      state.meterInstallEligibility?.reason ===
        InstallEligibilityReasons.INELIGIBLE_TOO_COMPLEX ||
      state.meterInstallEligibility?.reason ===
        InstallEligibilityReasons.INELIGIBLE_MOP_DISCREPANCY
    )
  },
  fullName(state) {
    return state.junifer?.customer?.forename && state.junifer?.customer?.surname
      ? `${state.junifer.customer.forename} ${state.junifer.customer.surname}`
      : ""
  },
  primaryEmail(state) {
    return state.junifer?.customer?.primaryContact.email || ""
  },
  primaryContact(state) {
    const primaryContact = state.junifer.customer.primaryContact
    return {
      firstName: primaryContact.forename || "",
      lastName: primaryContact.surname || "",
      telephone:
        primaryContact.phoneNumber1 ||
        primaryContact.phoneNumber2 ||
        primaryContact.phoneNumber3 ||
        "",
      email: primaryContact.email || "",
    }
  },
  address(state, getters) {
    if (!getters.meterpointAddress) return null

    const { address1, address2, address3 } = getters.meterpointAddress
    return [address1, address2, address3].filter(Boolean).join(", ")
  },
  city(state) {
    const city = Object.values(
      state.junifer.accounts[state.customerId].meterpoint_addresses
    )[0].address4
    // the backend is expecting a string so we need to return "Unknown" if the city is undefined
    return city || "Unknown"
  },
  postcode(state, getters) {
    return getters.meterpointAddress?.postcode || null
  },
  meterpointAddress(state) {
    const meterpointAddress = Object.values(
      state.junifer.accounts[state.customerId].meterpoint_addresses
    )

    return meterpointAddress?.length ? meterpointAddress[0] : null
  },
  validatedPostcode(state, getters) {
    if (!getters.postcode) return null
    return postcodeValidation(getters.postcode)
  },
  fullAddress(state, getters) {
    return `${getters.address}, ${getters.postcode}`
  },
  accountNumber(state) {
    return (
      state.accounts?.reduce((account, accounts) => {
        if (accounts.account.id == state.customerId)
          account = accounts.account.number
        return account
      }, 0) || null
    )
  },
  outCode(state) {
    if (!state.junifer) return null
    const postcode = state.junifer.accounts[state.customerId].postcode
    return postcode.replace(/\s+/g, "").slice(0, -3)
  },
  gasMeterpoint(state) {
    return findMeterpoint(state, "MPRN")
  },
  electricityMeterpoint(state) {
    return findMeterpoint(state, "MPAN")
  },
  mprn(state, getters) {
    return getters.gasMeterpoint?.identifier || ""
  },
  mpan(state, getters) {
    return getters.electricityMeterpoint?.identifier || ""
  },
  gasMeterSerialNumber(state, getters) {
    const serialNumber =
      getters.gasMeterpoint?.meters.find((meter) => meter.identifier)
        ?.identifier || ""

    return serialNumber.replace(/\s+/g, "")
  },
  electricityMeterSerialNumber(state, getters) {
    const serialNumber =
      getters.electricityMeterpoint?.meters.find((meter) => meter.identifier)
        ?.identifier || ""
    return serialNumber.replace(/\s+/g, "")
  },
  mopCode(state) {
    return state.meterInstallEligibility?.meterOperatorCode || ""
  },
  isEligibleForInstall(state) {
    const installEligibleValues = [
      InstallEligibilityReasons.ELIGIBLE_DUAL_EXCHANGE,
      InstallEligibilityReasons.ELIGIBLE_ELEC_EXCHANGE,
      InstallEligibilityReasons.ELIGIBLE_GAS_EXCHANGE,
    ]
    return installEligibleValues.includes(state.meterInstallEligibility?.reason)
  },
  gasMeterExchange(state) {
    return (
      state.meterInstallEligibility?.reason ===
        InstallEligibilityReasons.ELIGIBLE_DUAL_EXCHANGE ||
      state.meterInstallEligibility?.reason ===
        InstallEligibilityReasons.ELIGIBLE_GAS_EXCHANGE
    )
  },
  electricMeterExchange(state) {
    return (
      state.meterInstallEligibility?.reason ===
        InstallEligibilityReasons.ELIGIBLE_DUAL_EXCHANGE ||
      state.meterInstallEligibility?.reason ===
        InstallEligibilityReasons.ELIGIBLE_ELEC_EXCHANGE
    )
  },
  isDualFuelSite(state) {
    const { reason, elecMeterType, gasMeterType } =
      state.meterInstallEligibility ?? {}
    const meterPresent = (type) => type && type !== "NOT_PRESENT"
    return (
      reason === InstallEligibilityReasons.ELIGIBLE_DUAL_EXCHANGE ||
      (reason === InstallEligibilityReasons.ELIGIBLE_GAS_EXCHANGE &&
        meterPresent(elecMeterType)) ||
      (reason === InstallEligibilityReasons.ELIGIBLE_ELEC_EXCHANGE &&
        meterPresent(gasMeterType))
    )
  },
  meterTypeKey(state, getters) {
    const { gasMeterType, elecMeterType, reason } =
      state.meterInstallEligibility ?? {}
    if (!getters.isDualFuelSite) {
      if (getters.gasMeterExchange)
        return gasMeterType === "SMETS2" ? "SINGLE_GAS_SMETS2" : "SINGLE_GAS"
      if (getters.electricMeterExchange)
        return elecMeterType === "SMETS2" ? "SINGLE_ELEC_SMETS2" : "SINGLE_ELEC"
      return ""
    }
    if (reason === InstallEligibilityReasons.ELIGIBLE_GAS_EXCHANGE)
      return gasMeterType === "SMETS2"
        ? "DUAL_GAS_ONLY_SMETS2"
        : "DUAL_GAS_ONLY"
    if (reason === InstallEligibilityReasons.ELIGIBLE_ELEC_EXCHANGE)
      return elecMeterType === "SMETS2"
        ? "DUAL_ELEC_ONLY_SMETS2"
        : "DUAL_ELEC_ONLY"
    const toKeyType = (type) => (type === "TRADITIONAL" ? "TRAD" : type)
    const meterTypes = [toKeyType(gasMeterType), toKeyType(elecMeterType)]
      .sort()
      .join("-")
    return `DUAL_${meterTypes}`
  },
  jobNote(_state, getters) {
    switch (getters.meterTypeKey) {
      case "DUAL_TRAD-TRAD":
        return "Upgrade both trad meters on site to SMETS2"
      case "DUAL_SMETS1-TRAD":
        return "Upgrade both the S1 meter and trad meter on site to SMETS2"
      case "DUAL_SMETS1-SMETS1":
        return "Upgrade both S1 meters on site to SMETS2"
      case "DUAL_SMETS1-SMETS2":
        return "Replace the non-communicating S2 meter AND upgrade the S1 meter to SMETS2"
      case "DUAL_SMETS2-TRAD":
        return "Replace the non-communicating S2 meter AND upgrade the TRAD meter to SMETS2"
      case "DUAL_SMETS2-SMETS2":
        return "Replace both non-communicating S2 meters on site"
      case "DUAL_GAS_ONLY":
        return "Upgrade the GAS meter on site to SMETS2"
      case "DUAL_GAS_ONLY_SMETS2":
        return "Replace the non-communicating S2 GAS meter"
      case "DUAL_ELEC_ONLY":
        return "Upgrade the ELEC meter on site to SMETS2"
      case "DUAL_ELEC_ONLY_SMETS2":
        return "Replace the non-communicating S2 ELEC meter"
      case "SINGLE_GAS":
        return "Gas only site. Hot shoe required. Upgrade the meter on site to SMETS2"
      case "SINGLE_GAS_SMETS2":
        return "Gas only site. Hot shoe required. Replace the non-communicating S2 meter"
      case "SINGLE_ELEC":
        return "Elec only site. Upgrade the meter on site to SMETS2"
      case "SINGLE_ELEC_SMETS2":
        return "Elec only site. Replace the non-communicating S2 meter"
      default:
        return ""
    }
  },
  isEligibleForRepair(state) {
    const appointmentType = state.meterRepairEligibility?.appointmentType
    if (!appointmentType) return false
    return !appointmentType.includes("install")
  },
  appointmentType(state) {
    const appointmentType = state.meterRepairEligibility?.appointmentType
    return appointmentTypeMapping[appointmentType] || null
  },
}
